import React from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SxProps, Theme, Typography } from '@mui/material';
import { Control, Controller, Path, RegisterOptions } from 'react-hook-form';

type SelectFieldBaseProps = {
  helperText?: React.ReactNode;
  label: string;
  name: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  options: Array<{ label: string; value: string }>;
  sx: SxProps<Theme>;
  value: string;
};

const SelectFieldBase = React.forwardRef(function SelectFieldBase(
  { helperText, label, name, onChange, options, sx, value }: SelectFieldBaseProps,
  _ref: React.Ref<HTMLInputElement>,
) {
  return (
    <FormControl sx={sx}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} name={name} onChange={onChange} value={value}>
        {options.map(({ label, value }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
});

type SelectFieldProps<TFieldValues extends { [key: string]: unknown }> = {
  control: Control<TFieldValues>;
  helperText?: string;
  label: string;
  name: string;
  options: Array<{ label: string; value: string }>;
  rules?: Omit<RegisterOptions<TFieldValues>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  sx?: SxProps<Theme>;
};

function SelectField<TFieldValues extends { [key: string]: unknown }>({
  control,
  helperText,
  label,
  name,
  options,
  rules,
  sx,
}: SelectFieldProps<TFieldValues>) {
  return (
    <Controller
      control={control}
      name={name as Path<TFieldValues>}
      rules={rules}
      render={({ field: { name, onChange, value }, fieldState: { error } }) => {
        const displayHelperText = error?.message ? (
          <Typography variant="caption" color="error">
            {error.message}
          </Typography>
        ) : (
          helperText && <Typography variant="caption">{helperText}</Typography>
        );

        return (
          <SelectFieldBase
            helperText={displayHelperText}
            label={label}
            name={name as string}
            onChange={onChange}
            options={options}
            sx={sx}
            value={value as string}
          />
        );
      }}
    />
  );
}

export default SelectField;
